import { type User as Auth0User } from '@auth0/auth0-spa-js';
import React, { createContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { IVerantosUser } from '@verantos-dev/types-common';

import { getAuthClient, resetAuthClient } from './client';
import { getUser, hasAuthParams } from './utils';

interface AuthProviderProps {
  children: React.ReactNode;
}

export interface AuthContextValue {
  isAuthenticated: boolean;
  user: IVerantosUser;
  logout: (returnTo?: string) => Promise<void>;
}

// Create typed context
export const AuthContext = createContext<AuthContextValue>({
  isAuthenticated: false,
  user: {} as IVerantosUser,
  logout: async () => undefined,
});

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<Auth0User | undefined>();
  const intl = useIntl();

  useEffect(() => {
    let interval: NodeJS.Timer;
    const initAuth0 = async () => {
      try {
        const auth0Client = await getAuthClient();
        if (hasAuthParams()) {
          await auth0Client.handleRedirectCallback();
          window.history.replaceState({}, document.title, window.location.pathname);
        }

        const authenticated = await auth0Client.isAuthenticated();
        setIsAuthenticated(authenticated);

        if (authenticated) {
          const userProfile = await auth0Client.getUser();
          setUser(userProfile);
        } else {
          console.info('User is not authenticated, redirecting to login');
          auth0Client.loginWithRedirect();
        }

        // continuously check to see if user is logged in
        // this is necessary since error boundaries were not affective in handling errors
        // this is because of error handling that exists lower in the app such as
        // libs/frontend/data-access/data-access-cohorts/src/lib/useProjectCohortsNavigation.tsx
        // redirect to the root projects page instead of propagating an error.
        interval = setInterval(async () => {
          const isAuthenticated = await auth0Client.isAuthenticated();
          if (!isAuthenticated) {
            auth0Client.loginWithRedirect();
          }
        }, 1_000);
      } catch (error) {
        console.error('Error initializing Auth0:', error);
        throw error;
      }
    };

    (async () => await initAuth0())();
    return () => clearInterval(interval);
  }, [isAuthenticated]);

  const logout = async (returnTo: string | undefined) => {
    const auth0Client = await getAuthClient();
    await auth0Client.logout({ logoutParams: { returnTo: returnTo || window.location.origin } });
    resetAuthClient();
  };

  if (user === undefined) {
    return null;
  }

  return (
    <AuthContext.Provider value={{ isAuthenticated, user: getUser(user, intl), logout }}>
      {children}
    </AuthContext.Provider>
  );
};
